import { ActionIcon, Badge, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import {
  IconAlertCircle,
  IconEye,
  IconFileTypePdf,
  IconPlayerPause,
  IconRepeat,
  IconWallet,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { i18nKeys, type TranslateFunction } from 'locales';
import { Fragment, type MouseEvent, type ReactNode } from 'react';

// Everything the adornment predicates/renderers need that isn't on the row itself.
// Adornment applicability is data-driven off the row (e.g. the reminder-preview eye
// keys off the item's pending template reminder), so a new view-type needs no
// per-view flag here.
export type AdornmentContext = {
  t: TranslateFunction;
  groupedInvoices?: string[];
  isPreviewLoading: boolean;
  onShowReminderPreview: () => void;
  onShowPdf: () => void;
  onEditClient: (item: any) => (event: MouseEvent) => void;
};

type RowAdornment = {
  key: string;
  when: (item: any, ctx: AdornmentContext) => boolean;
  render: (item: any, ctx: AdornmentContext) => ReactNode;
};

const ICON_SIZE = 18;

const ROW_ADORNMENTS: RowAdornment[] = [
  {
    key: 'reminder-preview',
    // Shown for a row with a pending template reminder (no AI call scheduled). This
    // only ever holds in the reminders view, so it replaces the old isRemindersView flag.
    when: item => !item.next_ai_call && Boolean(item.preview_template),
    render: (_item, ctx) => (
      <Tooltip label={ctx.t(i18nKeys.SHOW_TEMPLATE)} withArrow>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          loading={ctx.isPreviewLoading}
          aria-label={ctx.t(i18nKeys.SHOW_TEMPLATE)}
          onClick={event => {
            event.stopPropagation();
            if (!ctx.isPreviewLoading) ctx.onShowReminderPreview();
          }}
        >
          <IconEye size={ICON_SIZE} />
        </ActionIcon>
      </Tooltip>
    ),
  },
  {
    key: 'missing-data',
    when: item => Boolean(item.address_missing || item.email_missing),
    render: (item, ctx) => (
      <Tooltip
        withArrow
        label={
          <Stack gap={2}>
            {item.address_missing && <Text size="xs">{ctx.t(i18nKeys.INCOMPLETE_ADRESS)}</Text>}
            {item.email_missing && <Text size="xs">{ctx.t(i18nKeys.MISSING_EMAIL)}</Text>}
          </Stack>
        }
      >
        <ActionIcon
          variant="subtle"
          color="red"
          size="sm"
          aria-label={ctx.t(i18nKeys.INCOMPLETE_ADRESS)}
          onClick={ctx.onEditClient(item)}
        >
          <IconAlertCircle size={14} />
        </ActionIcon>
      </Tooltip>
    ),
  },
  {
    key: 'pdf',
    when: item => Boolean(item.has_pdf),
    render: (_item, ctx) => (
      <Tooltip label={ctx.t(i18nKeys.INVOICE.VIEW_PDF)} withArrow>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label={ctx.t(i18nKeys.INVOICE.VIEW_PDF)}
          onClick={event => {
            event.stopPropagation();
            ctx.onShowPdf();
          }}
        >
          <IconFileTypePdf size={ICON_SIZE} />
        </ActionIcon>
      </Tooltip>
    ),
  },
  {
    key: 'grouped-invoices',
    when: (_item, ctx) => (ctx.groupedInvoices?.length ?? 0) >= 1,
    render: (_item, ctx) => (
      <Tooltip
        withArrow
        label={ctx.t(i18nKeys.TOOLTIP.IMMINENT_REMINDER_INVOICES, {
          references: (ctx.groupedInvoices ?? []).join(' '),
        })}
      >
        <Badge circle color="purple" variant="filled" size="md">
          {ctx.groupedInvoices?.length}
        </Badge>
      </Tooltip>
    ),
  },
  {
    key: 'off-balance',
    when: item => Boolean(item.off_balance_since ?? item.debtor_off_balance_since),
    render: (item, ctx) => {
      const label = ctx.t(i18nKeys.OFF_BALANCE, {
        since: dayjs(item.off_balance_since ?? item.debtor_off_balance_since).format('L'),
      });
      return (
        <Tooltip withArrow label={label}>
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            aria-label={label}
            onClick={ctx.onEditClient(item)}
          >
            <IconAlertCircle size={14} />
          </ActionIcon>
        </Tooltip>
      );
    },
  },
  {
    key: 'ai-retried',
    when: item => item.ai_call_retried_attempts_count > 0,
    render: (item, ctx) => (
      <Tooltip
        withArrow
        label={ctx.t(i18nKeys.TOOLTIP.AI_CALL_REPEATED, {
          count: item.ai_call_retried_attempts_count,
          repeat_number: item.ai_call_retried_attempts_count,
        })}
      >
        <ThemeIcon variant="transparent" color="orange" size="sm">
          <IconRepeat size={ICON_SIZE} />
        </ThemeIcon>
      </Tooltip>
    ),
  },
  {
    key: 'paused',
    when: item => Boolean(item.paused),
    render: (_item, ctx) => (
      <Tooltip label={ctx.t(i18nKeys.CLIENT.FILTER.PAUSED)} withArrow>
        <ThemeIcon variant="transparent" color="gray" size="sm">
          <IconPlayerPause size={ICON_SIZE} />
        </ThemeIcon>
      </Tooltip>
    ),
  },
  {
    key: 'payment-plan',
    when: item => Boolean(item.has_payment_plan),
    render: (_item, ctx) => (
      <Tooltip label={ctx.t(i18nKeys.ONGOING_PAYMENT_PLAN)} withArrow>
        <ThemeIcon variant="transparent" color="gray" size="sm">
          <IconWallet size={ICON_SIZE} />
        </ThemeIcon>
      </Tooltip>
    ),
  },
  {
    key: 'disputed',
    when: item => Boolean(item.disputed),
    render: (_item, ctx) => (
      <Tooltip label={ctx.t(i18nKeys.FOLLOW_UP.SUMMARY.CONFLICT)} withArrow>
        <ThemeIcon variant="transparent" color="red" size="sm">
          <IconAlertCircle size={ICON_SIZE} />
        </ThemeIcon>
      </Tooltip>
    ),
  },
];

export function RowAdornments({ item, ctx }: { item: any; ctx: AdornmentContext }) {
  return (
    <>
      {ROW_ADORNMENTS.filter(adornment => adornment.when(item, ctx)).map(adornment => (
        <Fragment key={adornment.key}>{adornment.render(item, ctx)}</Fragment>
      ))}
    </>
  );
}
